/**
 * Shapes of the offline sync between the kernel API and the device (ADR-0013, ADR-0034,
 * docs/architecture/offline.md). Server and web app share them.
 */

/** One record as it goes to the device. `data` is what the module chose to send. */
export interface OfflineRecord {
  id: string;
  version: number;
  /** ISO 8601. Orders the records for the incremental pull. */
  updatedAt: string;
  /** Soft-deleted on the server: the device removes it. */
  deleted: boolean;
  data: Record<string, unknown>;
}

export type OfflineCommandType = 'create' | 'update' | 'delete';

/** A change made on the device, replayed by the server in queue order. */
export interface OfflineCommand {
  /** UUIDv7 from the device. The server applies a command once, however often it arrives. */
  id: string;
  entity: string;
  type: OfflineCommandType;
  /** For `create` the device generates the ID (UUIDv7). */
  recordId: string;
  /** Version the change is based on, `null` for `create`. */
  baseVersion: number | null;
  /** New field values; empty for `delete`. */
  changes: Record<string, unknown>;
  /** Values of the changed fields as the device knew them, for `field-merge`. */
  base?: Record<string, unknown>;
  /** ISO 8601, when the person made the change on the device. */
  at: string;
  /**
   * Records whose creation must reach the server first, e.g. the inspection of a new finding.
   * The device holds the change back until then; in one push the server answers `pending` while
   * a creation it depends on did not apply.
   */
  dependsOn?: OfflineDependency[];
}

export interface OfflineDependency {
  entity: string;
  recordId: string;
}

/**
 * A record that widens what goes to the device, e.g. an object prepared for an inspection
 * (`OfflineDeclaration.pins`): records whose `field` holds the ID of a prepared record of
 * `entity` go to the device as well, whatever the scope says. With the own entity and the field
 * `id` the records are prepared themselves.
 */
export interface OfflinePin {
  entity: string;
  field: string;
}

/**
 * A record the records of an entity name and the device needs along, e.g. the customer of a
 * project (`OfflineDeclaration.references`): with each pull the device asks the source of
 * `entity` for the IDs its records hold in `field`, a UUID or a list of them.
 */
export interface OfflineReference {
  entity: string;
  field: string;
}

export type OfflineCommandResult =
  | { id: string; status: 'applied'; version: number }
  /** The record changed on the server; `current` is the server state the person compares with. */
  | { id: string; status: 'conflict'; current: OfflineRecord | null }
  /** The server refuses the change for good, e.g. `forbidden`, `not-found`, `too-old`. */
  | { id: string; status: 'rejected'; reason: string }
  /** Not processed this time (an earlier change to the record is open, or a server error). */
  | { id: string; status: 'pending' };

/** An entity the person may take offline, as `GET /api/sync/entities` returns it. */
export interface OfflineEntityInfo {
  entity: string;
  owner: string;
  label: string | null;
  mode: 'read' | 'read-write';
  /** `read-write` and the person holds the write permission. */
  writable: boolean;
  conflict: 'last-write-wins' | 'field-merge' | 'manual';
  maxAge: string;
  /**
   * Entities whose records this one follows, e.g. findings follow inspections. The device sends
   * the IDs it holds of them with each pull. Empty for module entities.
   */
  parents: string[];
  /** Prepared records that widen the scope; the device sends their IDs with each pull. */
  pins: OfflinePin[];
  /** Records of other entities the records name and the device fetches along. */
  references: OfflineReference[];
  /** Paths of file IDs in `data`, e.g. `fileId` or `photos[].fileId`; the device keeps those files. */
  fileFields: string[];
}

/** Body of `POST /api/sync/pull`. */
export interface OfflinePullRequest {
  entity: string;
  cursor: string | null;
  /** IDs the device holds, by parent entity (`OfflineEntityInfo.parents`). */
  parents?: Record<string, string[]>;
  /** IDs of prepared records, by pin entity (`OfflineEntityInfo.pins`). */
  pins?: Record<string, string[]>;
  /** Only the records of the given pins, ignoring the scope: the first pull after preparing. */
  pinsOnly?: boolean;
}

export interface OfflinePullResult {
  records: OfflineRecord[];
  /** Opaque; the next pull continues from here. */
  cursor: string;
  /** Another page is waiting. */
  more: boolean;
}

/** Records per pull and commands per push. */
export const OFFLINE_PULL_LIMIT = 200;
export const OFFLINE_PUSH_LIMIT = 100;
/** IDs per parent or pin entity in one pull. */
export const OFFLINE_PARENT_LIMIT = 2000;

/** How far a device clock may run ahead of the server. */
export const OFFLINE_CLOCK_SKEW_MS = 5 * 60_000;

const MAX_AGE = /^P(?!$)(?:(\d{1,3})W)?(?:(\d{1,3})D)?(?:T(\d{1,4})H)?$/;

/** `maxAge` of an offline declaration in milliseconds, e.g. `P7D`; `null` for anything else. */
export function offlineMaxAgeMs(maxAge: string): number | null {
  const match = MAX_AGE.exec(maxAge);
  if (!match) return null;
  const [, weeks, days, hours] = match;
  const totalHours = (Number(weeks ?? 0) * 7 + Number(days ?? 0)) * 24 + Number(hours ?? 0);
  return totalHours > 0 ? totalHours * 3_600_000 : null;
}
